import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Mail,
  Phone,
  MapPin,
  MessageCircle,
  Send,
  HelpCircle,
  Globe,
  Building,
  Clock,
  ChevronDown,
  Flag,
  Loader2,
  Plus,
  Minus
} from "lucide-react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/Button";
import CountryDropdown from "../components/ui/CountryDropdown";
import { useAppDispatch, useAppSelector } from "../redux/store";
import { fetchContactInfoByCountry, createContactUs } from "../redux/Slices/contactInfoSlice";
import { fetchCommonQuestions, fetchCommonQuestionsByCountry, clearCountryFaqs } from "../redux/Slices/CommonQuestionsSlice";
import { getAllCountries } from "../redux/Actions/AdminAction";
import { detectUserLocation } from "../redux/Actions/AuthAction";
import { getCountryById } from "../redux/Actions/LocationActions";
import { getCachedCountryPreference, setCachedCountryPreference } from "../lib/currencyUtils";
import { ContactUsTypeEnum } from "../modules/shared/model/contact-info.enum";
import { CommonQuestionCategoryEnum } from "../modules/shared/model/common-questions.enum";
import SharedHeader4 from "../Headers/SharedHeader4";
import SharedFooter3 from "../Footer/SharedFooter3";
import SharedHeader from "../Headers/SharedHeader";
import SharedHeader11 from "../Headers/SharedHeader11";

const ContactPage = () => {
  const { t } = useTranslation();
  const renderStartTime = performance.now();
  console.log("═══════════════════════════════════════════════════════");
  console.log("🎬 [COMPONENT MOUNT] ContactPage component rendering...", new Date().toISOString());
  console.log("═══════════════════════════════════════════════════════");
  
  const dispatch = useAppDispatch();
  const { current: contactInfo, loading, error } = useAppSelector((state: any) => state.contactInfos);
  const { loading: contactUsLoading, error: contactUsError } = useAppSelector((state: any) => state.contactInfos.contactUs);
  const { 
    commonQuestions, 
    loading: faqLoading,
    countryFaqs,
    countryFaqsLoading,
    countryFaqsError,
    countryFaqsTotalCount,
    countryFaqsCurrentPage,
    countryFaqsHasNextPage
  } = useAppSelector((state: any) => state.commonQuestions);
  const { countries } = useAppSelector((state: any) => state.admin || { countries: [] });
  
  console.log("📊 [INITIAL STATE] Countries from Redux:", {
    count: countries?.length || 0,
    hasCountries: !!(countries && countries.length > 0)
  });

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
    type: "1" // Default to General Support
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<{id: string; name: string; code: string} | null>(null);
  const [selectedCountryId, setSelectedCountryId] = useState<string>("");
  const [expandedFaq, setExpandedFaq] = useState<string | null>(null);
  const [showAllFaqs, setShowAllFaqs] = useState(false);
  const [isFaqSectionExpanded, setIsFaqSectionExpanded] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isDetectingCountry, setIsDetectingCountry] = useState(false);
  const [isFetchingData, setIsFetchingData] = useState(false); // Track entire data fetch process
  const [loadingMessage, setLoadingMessage] = useState("Loading..."); // Dynamic loading message
  const hasDetectedCountry = useRef(false); // Prevent duplicate detection
  const componentMountTime = useRef(renderStartTime); // Track component mount time

  // Debug: Track loader state changes
  useEffect(() => {
    console.log(`🔍 [LOADER STATE] isFetchingData changed to: ${isFetchingData}`, new Date().toISOString());
    console.log(`🔍 [LOADER STATE] loading (contactInfo): ${loading}`);
    console.log(`🔍 [LOADER STATE] countryFaqsLoading: ${countryFaqsLoading}`);
    console.log(`🔍 [LOADER STATE] Combined loader should be: ${loading || countryFaqsLoading || isFetchingData ? "VISIBLE" : "HIDDEN"}`);
  }, [isFetchingData, loading, countryFaqsLoading]);

  // Debug: Track countries loading (to find the 16-second delay)
  useEffect(() => {
    const timeSinceMountMs = performance.now() - componentMountTime.current;
    console.log("📊 [DATA STATE] Countries updated:", {
      count: countries?.length || 0,
      hasData: !!(countries && countries.length > 0),
      firstCountry: countries?.[0]?.name || "N/A",
      timestamp: new Date().toISOString(),
      timeSinceMount: `${timeSinceMountMs.toFixed(2)}ms (${(timeSinceMountMs / 1000).toFixed(2)}s)`
    });
    
    if (countries && countries.length > 0) {
      console.log(`✅ [MILESTONE] Countries loaded ${(timeSinceMountMs / 1000).toFixed(2)}s after component mount! This will trigger country detection.`);
    }
  }, [countries]);

  // Debug: Track contact info changes
  useEffect(() => {
    console.log("📊 [DATA STATE] Contact Info updated:", {
      hasContactInfo: !!contactInfo,
      email: contactInfo?.email || "N/A",
      phone: contactInfo?.phone || "N/A",
      country: contactInfo?.country?.name || "N/A",
      countryId: contactInfo?.country?.id || "N/A",
      timestamp: new Date().toISOString()
    });
  }, [contactInfo]);

  // Debug: Track FAQs changes
  useEffect(() => {
    console.log("📊 [DATA STATE] Country FAQs updated:", {
      count: countryFaqs?.length || 0,
      hasData: !!(countryFaqs && countryFaqs.length > 0),
      firstQuestion: countryFaqs?.[0]?.question || "N/A",
      timestamp: new Date().toISOString()
    });
  }, [countryFaqs]);

  // Debug: Track common questions changes
  useEffect(() => {
    console.log("📊 [DATA STATE] Common Questions updated:", {
      count: commonQuestions?.length || 0,
      hasData: !!(commonQuestions && commonQuestions.length > 0),
      firstQuestion: commonQuestions?.[0]?.question || "N/A",
      timestamp: new Date().toISOString()
    });
  }, [commonQuestions]);

  // Handle country selection (manual selection from dropdown)
  const handleCountrySelect = useCallback((country: any) => {
    if (country) {
      const selectionTime = performance.now();
      console.log("═══════════════════════════════════════════════════════");
      console.log("👆 [USER ACTION] Manually selected country:", country.name, "ID:", country.id, new Date().toISOString());
      console.log("═══════════════════════════════════════════════════════");
      
      const setCountryStartTime = performance.now();
      setSelectedCountry(country);
      setSelectedCountryId(country.id);
      const setCountryEndTime = performance.now();
      console.log(`⏱️ [TIMING] setState calls took ${(setCountryEndTime - setCountryStartTime).toFixed(2)}ms`);
      
      // Cache the manually selected country
      const cacheStartTime = performance.now();
      setCachedCountryPreference(country.id);
      const cacheEndTime = performance.now();
      console.log(`⏱️ [TIMING] Cache write took ${(cacheEndTime - cacheStartTime).toFixed(2)}ms`);
      console.log("💾 [CACHE] Saved to localStorage with key 'user_country_preference'");
      
      // Verify it was saved
      const saved = localStorage.getItem("user_country_preference");
      console.log("✔️ [VERIFY] localStorage contains:", saved);
      console.log(`⏱️ [TIMING] Total handleCountrySelect took ${(performance.now() - selectionTime).toFixed(2)}ms`);
    }
  }, []);

  // Sync selectedCountry with selectedCountryId - fetch country if not in loaded list
  // Similar to AddFundsModal lines 62-75
  useEffect(() => {
    const fetchCountryIfNeeded = async () => {
      console.log("🔄 Sync effect triggered. selectedCountryId:", selectedCountryId, "selectedCountry?.id:", selectedCountry?.id);
      
      if (!selectedCountryId) {
        console.log("⏭️ No selectedCountryId, skipping sync");
        return;
      }
      
      // If selectedCountry already matches, no need to fetch
      if (selectedCountry?.id === selectedCountryId) {
        console.log("✅ selectedCountry already matches selectedCountryId, no action needed");
        return;
      }
      
      console.log("🔍 Need to sync country. Checking loaded countries list...");
      console.log("📋 Countries in list:", countries?.length, "countries");
      
      // Check if country is in the loaded list
      if (countries && countries.length > 0) {
        const countryInList = countries.find((c: any) => c.id === selectedCountryId);
        if (countryInList) {
          console.log("✅ Found country in loaded list:", countryInList.name);
          setSelectedCountry({
            id: countryInList.id,
            name: countryInList.name,
            code: countryInList.initial || countryInList.code
          });
          return;
        } else {
          console.log("⚠️ Country ID", selectedCountryId, "not found in loaded list");
        }
      }
      
      // Country not in loaded list, fetch it separately and append to dropdown
      try {
        console.log("🌐 Fetching country by ID from API:", selectedCountryId);
        const country = await getCountryById(selectedCountryId);
        if (country) {
          console.log("✅ Successfully fetched country from API:", country.name);
          
          // Set as selected country
          setSelectedCountry({
            id: country.id,
            name: country.name,
            code: country.initial
          });
          
          // Append to countries list in Redux so it appears in dropdown
          console.log("📥 Appending country to dropdown list...");
          await dispatch(getAllCountries({
            page: 1,
            limit: 1,
            search: country.name,
            isShowLoader: false,
            append: true // Append to existing list
          }));
          console.log("✅ Country appended to dropdown");
        } else {
          console.log("⚠️ API returned empty country");
      }
    } catch (error) {
        console.error("❌ Error fetching country by ID:", error);
      }
    };
    
    fetchCountryIfNeeded();
  }, [selectedCountryId, countries, selectedCountry?.id, dispatch]);

  // Country detection effect - similar to AddFundsModal currency detection
  useEffect(() => {
    const detectAndSetCountry = async () => {
      // IMPORTANT: Prevent duplicate runs
      if (!countries || countries.length === 0 || hasDetectedCountry.current) {
        console.log("⏭️ [SKIP] Detection skipped. hasDetectedCountry:", hasDetectedCountry.current, "countries.length:", countries?.length);
        return;
      }

      console.log("🚀 [START] Country detection process starting...", new Date().toISOString());
      hasDetectedCountry.current = true; // Mark as started immediately
      setIsDetectingCountry(true);
      
      try {
        // Debug: Check what's in localStorage
        const rawCache = localStorage.getItem("user_country_preference");
        console.log("🔍 [CACHE CHECK] Raw localStorage value:", rawCache);
        
        // First check for cached preference
        const cachedCountryId = getCachedCountryPreference();
        console.log("📦 [CACHE CHECK] getCachedCountryPreference returned:", cachedCountryId);
        
        if (cachedCountryId) {
          console.log("✅ [CACHE HIT] Found cached country ID, setting selectedCountryId to:", cachedCountryId);
          // Set the ID, the sync effect will handle fetching if needed
          setSelectedCountryId(cachedCountryId);
          setIsDetectingCountry(false);
          return;
        }

        // No cache found - call backend API to detect location
        const detectionStartTime = performance.now();
        console.log("🌍 [API CALL] No cache found, calling detect-location API...", new Date().toISOString());
        
        const result = await dispatch(detectUserLocation({ isShowLoader: false }));
        
        const detectionEndTime = performance.now();
        console.log(`⏱️ [TIMING] detect-location API took ${(detectionEndTime - detectionStartTime).toFixed(2)}ms`);
        
        if (detectUserLocation.fulfilled.match(result)) {
          const locationData = result.payload;
          const countryCode = locationData?.countryCode;
          const countryName = locationData?.countryName;
          
          console.log("📍 [API RESPONSE] Backend detected location:", { countryCode, countryName });
          
          // Find country by code in the loaded countries
          let detectedCountry = countries.find((country: any) => 
            country.initial?.toUpperCase() === countryCode ||
            country.code?.toUpperCase() === countryCode
          );
          
          // If not found by code, try by name
          if (!detectedCountry && countryName) {
            detectedCountry = countries.find((country: any) => 
              country.name?.toLowerCase() === countryName.toLowerCase()
            );
          }
          
          // If still not found, search for specific country and append to dropdown
          if (!detectedCountry && countryCode) {
            console.log("⚠️ [SEARCH] Country not in loaded list, searching by code:", countryCode);
            const searchStartTime = performance.now();
            
            const searchResult = await dispatch(getAllCountries({
              page: 1,
              limit: 1,
              search: countryCode,
              isShowLoader: false,
              append: true, // Append to existing countries
            }));
            
            const searchEndTime = performance.now();
            console.log(`⏱️ [TIMING] Country search by code took ${(searchEndTime - searchStartTime).toFixed(2)}ms`);
            
            if (getAllCountries.fulfilled.match(searchResult)) {
              const searchedCountries = searchResult.payload?.data || searchResult.payload || [];
              console.log("📦 [SEARCH] Found", searchedCountries.length, "countries by code");
              detectedCountry = searchedCountries.find((country: any) => 
                country.initial?.toUpperCase() === countryCode ||
                country.code?.toUpperCase() === countryCode
              );
              
              if (detectedCountry) {
                console.log("✅ [SEARCH] Found country by code:", detectedCountry.name, "- appended to dropdown");
              }
              
              // If still not found, try by name
              if (!detectedCountry && countryName) {
                console.log("⚠️ [SEARCH] Searching by name:", countryName);
                const nameSearchStartTime = performance.now();
                
                const nameSearchResult = await dispatch(getAllCountries({
                  page: 1,
                  limit: 1,
                  search: countryName,
                  isShowLoader: false,
                  append: true, // Append to existing countries
                }));
                
                const nameSearchEndTime = performance.now();
                console.log(`⏱️ [TIMING] Country search by name took ${(nameSearchEndTime - nameSearchStartTime).toFixed(2)}ms`);
                
                if (getAllCountries.fulfilled.match(nameSearchResult)) {
                  const nameSearchedCountries = nameSearchResult.payload?.data || nameSearchResult.payload || [];
                  console.log("📦 [SEARCH] Found", nameSearchedCountries.length, "countries by name");
                  detectedCountry = nameSearchedCountries.find((country: any) => 
                    country.name?.toLowerCase() === countryName.toLowerCase()
                  );
                  
                  if (detectedCountry) {
                    console.log("✅ [SEARCH] Found country by name:", detectedCountry.name, "- appended to dropdown");
                  }
                }
              }
            }
          }
          
          if (detectedCountry) {
            console.log("✅ [SUCCESS] Country detected from API:", detectedCountry.name, "ID:", detectedCountry.id);
            console.log("📍 [DETECTION] Setting selectedCountryId to:", detectedCountry.id);
            setSelectedCountryId(detectedCountry.id);
            setCachedCountryPreference(detectedCountry.id);
            console.log("💾 [CACHE WRITE] Cached detected country ID:", detectedCountry.id);
          } else {
            console.log("⚠️ [WARNING] Could not find country from detection");
            console.log("🔍 [DEBUG] Searched for - Code:", countryCode, "Name:", countryName);
            console.log("🔄 [FALLBACK] Auto-selecting United States as default country...");
            
            // Auto-select United States as fallback
            try {
              const fallbackStartTime = performance.now();
              const usResult = await dispatch(getAllCountries({
                page: 1,
                limit: 1,
                search: "United States",
                isShowLoader: false,
                append: true
              }));
              
              const fallbackEndTime = performance.now();
              console.log(`⏱️ [TIMING] United States search took ${(fallbackEndTime - fallbackStartTime).toFixed(2)}ms`);
              
              if (getAllCountries.fulfilled.match(usResult)) {
                const usCountry = usResult.payload?.data?.find((country: any) => 
                  country.name?.toLowerCase().includes("united states") ||
                  country.name?.toLowerCase() === "united states" ||
                  country.initial?.toLowerCase() === "us"
                );
                
                if (usCountry) {
                  console.log("✅ [FALLBACK] Auto-selected United States, ID:", usCountry.id);
                  setSelectedCountryId(usCountry.id);
                  setCachedCountryPreference(usCountry.id);
                  console.log("💾 [CACHE WRITE] Cached United States as default country");
                } else {
                  console.warn("⚠️⚠️⚠️ [CRITICAL] Could not find United States country");
                }
              }
            } catch (fallbackError) {
              console.error("❌ [FALLBACK] Error auto-selecting United States:", fallbackError);
            }
          }
        } else {
          console.log("⚠️ [API ERROR] Location detection API failed or was rejected");
          console.log("🔄 [FALLBACK] Auto-selecting United States as default country...");
          
          // Auto-select United States when API fails
          try {
            const fallbackStartTime = performance.now();
            const usResult = await dispatch(getAllCountries({
              page: 1,
              limit: 1,
              search: "United States",
              isShowLoader: false,
              append: true
            }));
            
            const fallbackEndTime = performance.now();
            console.log(`⏱️ [TIMING] United States search took ${(fallbackEndTime - fallbackStartTime).toFixed(2)}ms`);
            
            if (getAllCountries.fulfilled.match(usResult)) {
              const usCountry = usResult.payload?.data?.find((country: any) => 
                country.name?.toLowerCase().includes("united states") ||
                country.name?.toLowerCase() === "united states" ||
                country.initial?.toLowerCase() === "us"
              );
              
              if (usCountry) {
                console.log("✅ [FALLBACK] Auto-selected United States after API error, ID:", usCountry.id);
                setSelectedCountryId(usCountry.id);
                setCachedCountryPreference(usCountry.id);
                console.log("💾 [CACHE WRITE] Cached United States as default country");
              } else {
                console.warn("⚠️⚠️⚠️ [CRITICAL] Could not find United States country");
              }
            }
          } catch (fallbackError) {
            console.error("❌ [FALLBACK] Error auto-selecting United States:", fallbackError);
          }
        }
        
      } catch (error) {
        console.error("❌ [EXCEPTION] Error detecting country:", error);
        console.log("🔄 [FALLBACK] Auto-selecting United States after exception...");
        
        // Auto-select United States when exception occurs
        try {
          const usResult = await dispatch(getAllCountries({
            page: 1,
            limit: 1,
            search: "United States",
            isShowLoader: false,
            append: true
          }));
          
          if (getAllCountries.fulfilled.match(usResult)) {
            const usCountry = usResult.payload?.data?.find((country: any) => 
              country.name?.toLowerCase().includes("united states") ||
              country.name?.toLowerCase() === "united states" ||
              country.initial?.toLowerCase() === "us"
            );
            
            if (usCountry) {
              console.log("✅ [FALLBACK] Auto-selected United States after exception, ID:", usCountry.id);
              setSelectedCountryId(usCountry.id);
              setCachedCountryPreference(usCountry.id);
              console.log("💾 [CACHE WRITE] Cached United States as default country");
            }
          }
        } catch (fallbackError) {
          console.error("❌ [FALLBACK] Error auto-selecting United States:", fallbackError);
        }
      } finally {
        setIsDetectingCountry(false);
        console.log("🏁 [END] Country detection process completed", new Date().toISOString());
      }
    };

    detectAndSetCountry();
  }, [countries.length, dispatch]); // Depend on countries.length and dispatch

  // Fetch initial data on component mount
  useEffect(() => {
    const fetchInitialData = async () => {
      const initStartTime = performance.now();
      const timeSinceMount = initStartTime - componentMountTime.current;
      
      console.log("═══════════════════════════════════════════════════════");
      console.log("🚀 [START] Fetching initial data...", new Date().toISOString());
      console.log(`⏱️ [TIMING] Started ${timeSinceMount.toFixed(2)}ms after component mount`);
      console.log("═══════════════════════════════════════════════════════");
      
      setIsInitialLoading(true);
      try {
        // Fetch countries first (needed for country detection)
        if (!countries || countries.length === 0) {
          const countriesStartTime = performance.now();
          console.log("📡 [INIT] Fetching countries list...");
          await dispatch(getAllCountries({
            page: 1,
            limit: 1,
            isShowLoader: false,
            append: false
          })).unwrap();
          const countriesEndTime = performance.now();
          console.log(`⏱️ [TIMING] fetchCountries took ${(countriesEndTime - countriesStartTime).toFixed(2)}ms`);
        }
        
        // Fetch global common questions
        const faqStartTime = performance.now();
        await dispatch(fetchCommonQuestions({
          page: 1,
          limit: 5,
          sortBy: "createdAt",
          sortOrder: "desc"
        })).unwrap();
        
        const faqEndTime = performance.now();
        console.log(`⏱️ [TIMING] fetchCommonQuestions took ${(faqEndTime - faqStartTime).toFixed(2)}ms`);
      } catch (error) {
        console.error("❌ [ERROR] Error fetching initial data:", error);
      } finally {
        setIsInitialLoading(false);
        const initEndTime = performance.now();
        console.log(`⏱️ [TIMING] Total initial data fetch took ${(initEndTime - initStartTime).toFixed(2)}ms`);
        console.log("🏁 [END] Initial data fetch completed", new Date().toISOString());
      }
    };

    fetchInitialData();
  }, [dispatch]);

  // Fetch contact info and FAQs when country is selected (either auto-detected or manually selected)
  useEffect(() => {
    // Helper function to fetch United States country as fallback from backend
    const fetchFallbackCountry = async () => {
      const startTime = performance.now();
      try {
        console.log("🔍 [FALLBACK] Searching for United States country from backend...", new Date().toISOString());
        
        const result = await dispatch(getAllCountries({
          page: 1,
          limit: 1,
          search: "United States",
          isShowLoader: false,
          append: true // Append to dropdown so it's available for selection
        }));
        
        const endTime = performance.now();
        console.log(`⏱️ [TIMING] Fallback country search took ${(endTime - startTime).toFixed(2)}ms`);
        
        if (getAllCountries.fulfilled.match(result)) {
          const usCountry = result.payload?.data?.find((country: any) => 
            country.name?.toLowerCase().includes("united states") ||
            country.name?.toLowerCase() === "united states" ||
            country.initial?.toLowerCase() === "us"
          );
          
          if (usCountry) {
            console.log("✅ [FALLBACK] Found United States country, ID:", usCountry.id, new Date().toISOString());
            console.log("📥 [FALLBACK] Country appended to dropdown list");
          } else {
            console.log("⚠️ [FALLBACK] United States country not found in search results", new Date().toISOString());
          }
          
          return usCountry;
        }
        return null;
      } catch (error) {
        const endTime = performance.now();
        console.error(`❌ [FALLBACK] Fetching United States country failed after ${(endTime - startTime).toFixed(2)}ms:`, error);
        return null;
      }
    };

    // Separate function to fetch contact info with independent fallback
    const fetchContactInfo = async (countryId: string, countryName: string) => {
      const startTime = performance.now();
      try {
        console.log("📡 [CONTACT INFO] Fetching for country ID:", countryId, `(${countryName})`, new Date().toISOString());
        setLoadingMessage(`Fetching contact information for ${countryName}...`);
        
        const contactInfoResult = await dispatch(fetchContactInfoByCountry(countryId)).unwrap();
        
        const endTime = performance.now();
        console.log(`⏱️ [TIMING] Contact info fetch took ${(endTime - startTime).toFixed(2)}ms`);
        console.log("📦 [CONTACT INFO] Received:", {
          hasData: !!contactInfoResult,
          email: contactInfoResult?.email || "N/A",
          phone: contactInfoResult?.phone || "N/A",
          country: contactInfoResult?.country?.name || "N/A"
        });
        
        // Validate contact info
        const hasValidContactInfo = contactInfoResult && 
                                    typeof contactInfoResult === "object" &&
                                    (contactInfoResult.email || contactInfoResult.phone || contactInfoResult.whatsapp);
        
        if (hasValidContactInfo) {
          console.log("✅ [CONTACT INFO] Valid contact info found for", countryName);
          return true; // Success
        } else {
          console.log("⚠️ [CONTACT INFO] No valid contact info for", countryName, "- trying fallback...");
          
          // Fetch fallback country
          const fallbackCountry = await fetchFallbackCountry();
          
          if (fallbackCountry?.id) {
            const fallbackStartTime = performance.now();
            console.log("📡 [CONTACT INFO FALLBACK] Fetching for United States (ID:", fallbackCountry.id, ")");
            setLoadingMessage("Fetching default contact information...");
            
            const fallbackContactInfo = await dispatch(fetchContactInfoByCountry(fallbackCountry.id)).unwrap();
            
            const fallbackEndTime = performance.now();
            console.log(`⏱️ [TIMING] Fallback contact info fetch took ${(fallbackEndTime - fallbackStartTime).toFixed(2)}ms`);
            console.log("📦 [CONTACT INFO FALLBACK] Received:", {
              hasData: !!fallbackContactInfo,
              email: fallbackContactInfo?.email || "N/A",
              phone: fallbackContactInfo?.phone || "N/A"
            });
            
            const hasValidFallbackContactInfo = fallbackContactInfo && 
                                                typeof fallbackContactInfo === "object" &&
                                                (fallbackContactInfo.email || fallbackContactInfo.phone || fallbackContactInfo.whatsapp);
            
            if (!hasValidFallbackContactInfo) {
              console.warn("⚠️⚠️⚠️ [CRITICAL] United States also has no valid contact info!");
            }
          } else {
            console.error("❌ [CONTACT INFO FALLBACK] Could not find fallback country");
          }
          
          return false; // Had to use fallback
        }
      } catch (error) {
        console.error("❌ [CONTACT INFO] Error fetching contact info:", error);
        
        // Try fallback on error
        try {
          const fallbackCountry = await fetchFallbackCountry();
          if (fallbackCountry?.id) {
            console.log("📡 [CONTACT INFO FALLBACK] Fetching after error for United States");
            setLoadingMessage("Loading default contact information...");
            await dispatch(fetchContactInfoByCountry(fallbackCountry.id)).unwrap();
          }
        } catch (fallbackError) {
          console.error("❌ [CONTACT INFO FALLBACK] Fallback also failed:", fallbackError);
        }
        
        return false;
      }
    };

    // Separate function to fetch FAQs with independent fallback
    const fetchFaqs = async (countryId: string, countryName: string) => {
      const startTime = performance.now();
      try {
        console.log("📡 [FAQs] Fetching for country ID:", countryId, `(${countryName})`, new Date().toISOString());
        setLoadingMessage(`Fetching FAQs for ${countryName}...`);
        
        const faqsResult = await dispatch(fetchCommonQuestionsByCountry({
          countryId: countryId,
          filters: {
            page: 1,
            limit: 50,
            sortBy: "createdAt",
            sortOrder: "desc"
          }
        })).unwrap();
        
        const endTime = performance.now();
        console.log(`⏱️ [TIMING] FAQs fetch took ${(endTime - startTime).toFixed(2)}ms`);
        console.log("📦 [FAQs] Received:", {
          dataCount: faqsResult?.data?.length || 0,
          totalCount: faqsResult?.totalCount || 0,
          firstQuestion: faqsResult?.data?.[0]?.question || "N/A"
        });
        
        // Check if FAQs have data
        const hasValidFaqs = faqsResult?.data && faqsResult.data.length > 0;
        
        if (hasValidFaqs) {
          console.log("✅ [FAQs] Found", faqsResult.data.length, "FAQs for", countryName);
          return true; // Success
        } else {
          console.log("⚠️ [FAQs] No FAQs found for", countryName, "- trying fallback...");
          
          // Fetch fallback country
          const fallbackCountry = await fetchFallbackCountry();
          
          if (fallbackCountry?.id) {
            const fallbackStartTime = performance.now();
            console.log("📡 [FAQs FALLBACK] Fetching for United States (ID:", fallbackCountry.id, ")");
            setLoadingMessage("Fetching default FAQs...");
            
            const fallbackFaqsResult = await dispatch(fetchCommonQuestionsByCountry({
              countryId: fallbackCountry.id,
              filters: {
                page: 1,
                limit: 50,
                sortBy: "createdAt",
                sortOrder: "desc"
              }
            })).unwrap();
            
            const fallbackEndTime = performance.now();
            console.log(`⏱️ [TIMING] Fallback FAQs fetch took ${(fallbackEndTime - fallbackStartTime).toFixed(2)}ms`);
            console.log("📦 [FAQs FALLBACK] Received:", {
              dataCount: fallbackFaqsResult?.data?.length || 0,
              totalCount: fallbackFaqsResult?.totalCount || 0
            });
            
            const hasValidFallbackFaqs = fallbackFaqsResult?.data && fallbackFaqsResult.data.length > 0;
            
            if (!hasValidFallbackFaqs) {
              console.warn("⚠️ [FAQs FALLBACK] United States also has no FAQs - will show global FAQs");
            }
          } else {
            console.error("❌ [FAQs FALLBACK] Could not find fallback country");
          }
          
          return false; // Had to use fallback
        }
      } catch (error) {
        console.error("❌ [FAQs] Error fetching FAQs:", error);
        
        // Try fallback on error
        try {
          const fallbackCountry = await fetchFallbackCountry();
          if (fallbackCountry?.id) {
            console.log("📡 [FAQs FALLBACK] Fetching after error for United States");
            setLoadingMessage("Loading default FAQs...");
            await dispatch(fetchCommonQuestionsByCountry({
              countryId: fallbackCountry.id,
              filters: {
                page: 1,
                limit: 50,
                sortBy: "createdAt",
                sortOrder: "desc"
              }
            })).unwrap();
          }
        } catch (fallbackError) {
          console.error("❌ [FAQs FALLBACK] Fallback also failed:", fallbackError);
        }
        
        return false;
      }
    };

    const fetchDataForCountry = async (countryId: string, countryName: string) => {
      const totalStartTime = performance.now();
      
      console.log("═══════════════════════════════════════════════════════");
      console.log("🚀 [START] Fetching data for:", countryName, "(ID:", countryId, ")", new Date().toISOString());
      console.log("═══════════════════════════════════════════════════════");
      
      setIsFetchingData(true);
      
      try {
        // Fetch contact info and FAQs independently (with their own fallback logic)
        const [contactInfoSuccess, faqsSuccess] = await Promise.all([
          fetchContactInfo(countryId, countryName),
          fetchFaqs(countryId, countryName)
        ]);
        
        console.log("📊 [RESULTS]", {
          contactInfo: contactInfoSuccess ? "Found for selected country" : "Used fallback",
          faqs: faqsSuccess ? "Found for selected country" : "Used fallback or global"
        });
        
      } catch (error) {
        console.error("❌ [ERROR] Unexpected error in fetchDataForCountry:", error);
      } finally {
        const totalEndTime = performance.now();
        console.log(`⏱️ [TIMING] Total data fetch took ${(totalEndTime - totalStartTime).toFixed(2)}ms`);
        console.log("🏁 [END] Data fetch completed", new Date().toISOString());
        
        setIsFetchingData(false);
        
        // Log final state
        setTimeout(() => {
          console.log("═══════════════════════════════════════════════════════");
          console.log("📊 [FINAL STATE] After data fetch:");
          console.log("  Contact Info:", {
            hasData: !!contactInfo,
            email: contactInfo?.email || "N/A",
            country: contactInfo?.country?.name || "N/A"
          });
          console.log("  Country FAQs:", {
            count: countryFaqs?.length || 0
          });
          console.log("  Selected Country:", {
            name: selectedCountry?.name || "N/A",
            id: selectedCountry?.id || "N/A"
          });
          console.log("═══════════════════════════════════════════════════════");
        }, 100);
      }
    };

    if (selectedCountry?.id) {
      console.log("═══════════════════════════════════════════════════════");
      console.log("🌍 [TRIGGER] Country selected:", selectedCountry.name, "ID:", selectedCountry.id, new Date().toISOString());
      console.log("═══════════════════════════════════════════════════════");
      
      // Clear FAQs before fetching new data
      dispatch(clearCountryFaqs());
      
      // Fetch data for selected country
      fetchDataForCountry(selectedCountry.id, selectedCountry.name);
    }
  }, [selectedCountry?.id, dispatch]);


  // Handle FAQ accordion toggle
  const toggleFaq = (faqId: string) => {
    setExpandedFaq(expandedFaq === faqId ? null : faqId);
  };

  // Handle show more FAQs
  const handleShowMoreFaqs = useCallback(() => {
    if (selectedCountry?.id && countryFaqsHasNextPage) {
      dispatch(fetchCommonQuestionsByCountry({
        countryId: selectedCountry.id,
        filters: {
          page: countryFaqsCurrentPage + 1,
          limit: 50,
          sortBy: "createdAt",
          sortOrder: "desc"
        }
      }));
    }
  }, [selectedCountry?.id, countryFaqsHasNextPage, countryFaqsCurrentPage, dispatch]);

  // Handle category selection
  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
  };

  // Filter FAQs by category
  const getFilteredFaqs = () => {
    const faqs = selectedCountry ? countryFaqs : commonQuestions;
    if (!faqs || faqs.length === 0) return [];
    
    if (selectedCategory === "all") {
      return faqs;
    }
    
    const categoryId = parseInt(selectedCategory);
    return faqs.filter(faq => Number(faq.category?.id) === categoryId);
  };

  // Get available categories from FAQs
  const getAvailableCategories = () => {
    const faqs = selectedCountry ? countryFaqs : commonQuestions;
    if (!faqs || faqs.length === 0) return [];
    
    const categories = new Set<number>();
    faqs.forEach(faq => {
      if (faq.category?.id) {
        categories.add(Number(faq.category.id));
      }
    });
    
    return Array.from(categories).map(categoryId => {
      const categoryName = Object.keys(CommonQuestionCategoryEnum).find(
        key => CommonQuestionCategoryEnum[key as keyof typeof CommonQuestionCategoryEnum] === categoryId
      );
      return { id: categoryId, name: categoryName || `Category ${categoryId}` };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const contactUsData = {
        name: formData.name || undefined,
        email: formData.email || undefined,
        subject: formData.subject || undefined,
        message: formData.message || undefined,
        type: {
          id: parseInt(formData.type)
        }
      };

      await dispatch(createContactUs(contactUsData)).unwrap();
      setSubmitSuccess(true);
      setFormData({ name: "", email: "", subject: "", message: "", type: "1" });
      setTimeout(() => setSubmitSuccess(false), 3000);
    } catch (error) {
      console.error("Failed to submit contact form:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle contact actions
  const handleEmailClick = (email: string) => {
    if (email && email !== "Not available") {
      window.open(`mailto:${email}`, "_blank");
    }
  };

  const handlePhoneClick = (phone: string) => {
    if (phone && phone !== "Not available") {
      window.open(`tel:${phone}`, "_self");
    }
  };

  const handleWhatsAppClick = (whatsapp: string) => {
    if (whatsapp && whatsapp !== "Not available") {
      // Clean the phone number (remove spaces, dashes, etc.)
      const cleanNumber = whatsapp.replace(/[\s\-()]/g, "");
      window.open(`https://wa.me/${cleanNumber}`, "_blank");
    }
  };

  // Generate contact info from API data
  const contactInfoCards = contactInfo ? [
    {
      icon: <Mail className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />,
      title: t("contactPage.contactCards.emailUs"),
      content: contactInfo.email || t("contactPage.notAvailable"),
      description: t("contactPage.contactCards.emailSupport"),
      onClick: () => handleEmailClick(contactInfo.email),
      clickable: contactInfo.email && contactInfo.email !== t("contactPage.notAvailable")
    },
    {
      icon: <Phone className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />,
      title: t("contactPage.contactCards.callUs"),
      content: contactInfo.phone || t("contactPage.notAvailable"),
      description: t("contactPage.contactCards.phoneSupport"),
      onClick: () => handlePhoneClick(contactInfo.phone),
      clickable: contactInfo.phone && contactInfo.phone !== t("contactPage.notAvailable")
    },
    {
      icon: <MessageCircle className="h-5 w-5 sm:h-6 sm:w-6 text-purple-600" />,
      title: t("contactPage.contactCards.whatsapp"),
      content: contactInfo.whatsapp || t("contactPage.notAvailable"),
      description: t("contactPage.contactCards.whatsappSupport"),
      onClick: () => handleWhatsAppClick(contactInfo.whatsapp),
      clickable: contactInfo.whatsapp && contactInfo.whatsapp !== t("contactPage.notAvailable")
    },
    {
      icon: <MapPin className="h-5 w-5 sm:h-6 sm:w-6 text-red-600" />,
      title: t("contactPage.contactCards.visitUs"),
      content: contactInfo.address || t("contactPage.notAvailable"),
      description: contactInfo.city && contactInfo.state
        ? `${contactInfo.city}, ${contactInfo.state}`
        : contactInfo.city || contactInfo.state || t("contactPage.contactCards.addressNotAvailable"),
      onClick: null,
      clickable: false
    }
  ] : [];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" style={{ textAlign: "left" }}>
      <SharedHeader11 />

      {/* Main Content Wrapper */}
      <div className="flex-1">
      {/* Hero Section */}
      <div className="bg-blue-600 text-white py-8 sm:py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-2xl sm:text-3xl font-bold mb-4">{t("contactPage.hero.title")}</h1>
          <p className="text-base sm:text-lg md:text-xl text-blue-100 max-w-2xl mx-auto">
            {t("contactPage.hero.subtitle")}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8 sm:py-12">
        {/* Global Initial Loader */}
        {(isInitialLoading || isDetectingCountry) && (
          <div className="flex flex-col items-center justify-center py-16">
            <Loader2 className="h-12 w-12 animate-spin text-blue-600 mb-4" />
            <p className="text-lg text-gray-600 font-medium">
              {isDetectingCountry ? t("contactPage.loading.settingUpLocation") : t("contactPage.loading.loadingContactInfo")}
            </p>
            <p className="text-sm text-gray-500 mt-2">{t("contactPage.loading.pleaseWait")}</p>
          </div>
        )}

        {!isInitialLoading && !isDetectingCountry && (
          <>
            {/* Country Selection */}
            <div className="mb-6 sm:mb-8">
              <div className="flex items-center justify-center mb-4">
                <Globe className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 mr-2" />
                <h2 className="text-lg sm:text-xl font-semibold">{t("contactPage.countrySelection.title")}</h2>
              </div>
              <div className="max-w-xs mx-auto">
                <CountryDropdown
                  selectedCountry={selectedCountry}
                  onCountrySelect={handleCountrySelect}
                  placeholder={t("contactPage.countrySelection.placeholder")}
                  disabled={isDetectingCountry}
                />
                {isDetectingCountry && (
                  <p className="mt-2 text-xs text-center text-gray-500">
                    {t("contactPage.countrySelection.loadingLocation")}
                  </p>
                )}
              </div>
            </div>

        {/* Contact Info Cards */}
        {(loading || countryFaqsLoading || isFetchingData) ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-12 w-12 animate-spin text-blue-600 mb-4" />
            <span className="text-lg text-gray-700 font-medium mb-2">
              {loadingMessage}
            </span>
            <span className="text-sm text-gray-500">
              {t("contactPage.loading.pleaseWaitMoment")}
            </span>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg max-w-md mx-auto">
              {error}
            </div>
          </div>
        ) : contactInfoCards.length > 0 ? (
          <>
            {selectedCountry && !contactInfo && (
              <div className="mb-4 text-center">
                <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-lg max-w-2xl mx-auto">
                  <p className="text-sm font-medium">
                    {t("contactPage.messages.showingDefaultInfo", { country: selectedCountry.name })}
                  </p>
                </div>
              </div>
            )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 mb-8 sm:mb-12">
            {contactInfoCards.map((item, index) => (
              <Card
                key={index}
                className={`p-4 sm:p-6 text-center transition-all duration-200 ${item.clickable
                    ? "hover:shadow-lg hover:scale-105 cursor-pointer hover:bg-blue-50"
                    : "hover:shadow-lg"
                  }`}
                onClick={item.onClick}
              >
                <div className="flex justify-center mb-3 sm:mb-4">
                  {item.icon}
                </div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2">{item.title}</h3>
                <p className={`text-sm sm:text-base mb-1 ${item.clickable ? "text-blue-600 hover:text-blue-800" : "text-gray-900"
                  }`}>
                  {item.content}
                </p>
                <p className="text-xs sm:text-sm text-gray-600">{item.description}</p>
                {item.clickable && (
                  <p className="text-xs text-blue-500 mt-2 font-medium">
                    {t("contactPage.contactCards.clickTo")} {item.title.toLowerCase()}
                  </p>
                )}
              </Card>
            ))}
          </div>
          </>
        ) : selectedCountry ? (
          <div className="text-center py-12">
            <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded-lg max-w-md mx-auto">
              <p className="font-medium mb-2">{t("contactPage.messages.noContactInfoAvailable")}</p>
              <p className="text-sm">
                {t("contactPage.messages.contactInfoNotAvailableForCountry", { country: selectedCountry.name })}
              </p>
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg max-w-md mx-auto">
              {t("contactPage.messages.selectCountryToView")}
            </div>
          </div>
        )}

        {/* Common Questions Section - Full Width */}
        {(selectedCountry && countryFaqs && countryFaqs.length > 0) || (commonQuestions && commonQuestions.length > 0) ? (
          <div className="mb-8 sm:mb-12">
            <Card className="border border-gray-200 rounded-lg">
              <button
                onClick={() => setIsFaqSectionExpanded(!isFaqSectionExpanded)}
                className="w-full px-4 py-3 sm:px-6 sm:py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <h2 className="text-xl sm:text-2xl font-semibold flex items-center gap-2">
                  <HelpCircle className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
                  {t("contactPage.faqs.title")}
                  {selectedCountry && (
                    <span className="text-sm font-normal text-gray-600 ml-2">
                      {t("contactPage.faqs.forCountry", { country: selectedCountry.name })}
                    </span>
                  )}
                </h2>
                {isFaqSectionExpanded ? (
                  <Minus className="h-5 w-5 sm:h-6 sm:w-6 text-gray-500 flex-shrink-0" />
                ) : (
                  <Plus className="h-5 w-5 sm:h-6 sm:w-6 text-gray-500 flex-shrink-0" />
                )}
              </button>
              
              {isFaqSectionExpanded && (
                <div className="px-4 py-3 sm:px-6 sm:py-4 border-t border-gray-200 bg-gray-50">
                  {(countryFaqsLoading || faqLoading || isFetchingData) ? (
                    <div className="flex flex-col items-center justify-center py-8">
                      <Loader2 className="h-8 w-8 animate-spin text-blue-600 mb-3" />
                      <span className="text-base text-gray-700 font-medium mb-1">
                        {loadingMessage}
                      </span>
                      <span className="text-xs text-gray-500">
                        {t("contactPage.loading.pleaseWaitMoment")}
                      </span>
                    </div>
                  ) : countryFaqsError ? (
                    <div className="text-center py-8">
                      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg max-w-md mx-auto">
                        {countryFaqsError}
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Show info message if showing global FAQs instead of country-specific */}
                      {selectedCountry && (!countryFaqs || countryFaqs.length === 0) && commonQuestions && commonQuestions.length > 0 && (
                        <div className="mb-4">
                          <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-lg">
                            <p className="text-sm font-medium">
                              {t("contactPage.faqs.showingGlobalFaqs", { country: selectedCountry.name })}
                            </p>
                          </div>
                        </div>
                      )}
                      {/* Category Filter Dropdown */}
                      <div className="flex items-center gap-4">
                        <label className="text-sm font-medium text-gray-700">
                          {t("contactPage.faqs.filterByCategory")}
                        </label>
                        <select
                          value={selectedCategory}
                          onChange={(e) => handleCategoryChange(e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="all">{t("contactPage.faqs.allCategories")}</option>
                          {getAvailableCategories().map((category) => (
                            <option key={Number(category.id)} value={Number(category.id)}>
                              {category.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Filtered FAQs */}
                      <div className="space-y-2">
                        {getFilteredFaqs().map((faq) => (
                          <div key={faq.id} className="border border-gray-200 rounded-lg">
                            <button
                              onClick={() => toggleFaq(faq.id)}
                              className="w-full px-4 py-3 sm:px-6 sm:py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                            >
                              <div className="flex-1 min-w-0">
                                <h3 className="font-medium text-sm sm:text-base pr-4">
                                  {faq.question}
                                </h3>
                                {faq.category && (
                                  <p className="text-xs text-gray-500 mt-1">
                                    {faq.category.name}
                                  </p>
                                )}
                              </div>
                              {expandedFaq === faq.id ? (
                                <Minus className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500 flex-shrink-0" />
                              ) : (
                                <Plus className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500 flex-shrink-0" />
                              )}
                            </button>
                            {expandedFaq === faq.id && (
                              <div className="px-4 py-3 sm:px-6 sm:py-4 border-t border-gray-200 bg-gray-50">
                                <p className="text-sm sm:text-base text-gray-700 whitespace-pre-line">
                                  {faq.answer}
                                </p>
                              </div>
                            )}
                          </div>
                        ))}
                        
                        {/* No FAQs message */}
                        {getFilteredFaqs().length === 0 && (
                          <div className="text-center py-8">
                            <div className="bg-gray-50 border border-gray-200 text-gray-600 px-4 py-3 rounded-lg max-w-md mx-auto">
                              {t("contactPage.faqs.noFaqsForCategory")}
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Show More Button for Country FAQs */}
                      {countryFaqs && countryFaqs.length > 0 && countryFaqsHasNextPage && (
                        <div className="text-center pt-4">
                          <Button
                            onClick={handleShowMoreFaqs}
                            variant="outline"
                            disabled={countryFaqsLoading}
                            className="px-6 py-2"
                          >
                            {countryFaqsLoading ? (
                              <>
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                {t("contactPage.faqs.loading")}
                              </>
                            ) : (
                              t("contactPage.faqs.showMoreFaqs")
                            )}
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </Card>
          </div>
        ) : null}

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-12">
          {/* Contact Form */}
          <Card className="p-4 sm:p-6">
            <h2 className="text-xl sm:text-2xl font-semibold mb-4 sm:mb-6">{t("contactPage.form.title")}</h2>
            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    {t("contactPage.form.yourName")}
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-2 py-1 sm:p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    {t("contactPage.form.emailAddress")}
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-2 py-1 sm:p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  {t("contactPage.form.inquiryType")}
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  className="w-full px-2 py-1 sm:p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                >
                  <option value="1">{t("contactPage.form.inquiryTypes.generalSupport")}</option>
                  <option value="2">{t("contactPage.form.inquiryTypes.technicalSupport")}</option>
                  <option value="3">{t("contactPage.form.inquiryTypes.billingQuestion")}</option>
                  <option value="4">{t("contactPage.form.inquiryTypes.partnershipOpportunity")}</option>
                  <option value="5">{t("contactPage.form.inquiryTypes.feedback")}</option>
                </select>
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  {t("contactPage.form.subject")}
                </label>
                <input
                  type="text"
                  required
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  className="w-full px-2 py-1 sm:p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  {t("contactPage.form.message")}
                </label>
                <textarea
                  required
                  rows={4}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full px-2 py-1 sm:p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>
              <div>
                <Button
                  type="submit"
                  disabled={isSubmitting || contactUsLoading}
                  className="w-full flex items-center justify-center gap-2 text-sm sm:text-base"
                >
                  {isSubmitting || contactUsLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {t("contactPage.form.processing")}
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" /> {t("contactPage.form.sendMessage")}
                    </>
                  )}
                </Button>
              </div>
              {submitSuccess && (
                <div className="p-3 sm:p-4 bg-green-50 text-green-700 rounded-lg text-sm">
                  {t("contactPage.form.successMessage")}
                </div>
              )}
              {contactUsError && (
                <div className="p-3 sm:p-4 bg-red-50 text-red-700 rounded-lg text-sm">
                  {contactUsError}
                </div>
              )}
            </form>
          </Card>

          {/* FAQ & Additional Info */}
          <div className="space-y-4 sm:space-y-6">
            <Card className="p-4 sm:p-6">
              <h2 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4 flex items-center gap-2">
                <HelpCircle className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" /> {t("contactPage.quickHelp.title")}
              </h2>
              <div className="space-y-3 sm:space-y-4">
                <div>
                  <h3 className="font-medium text-sm sm:text-base mb-2">{t("contactPage.quickHelp.businessHours")}</h3>
                  <div className="space-y-2 text-xs sm:text-sm">
                    {contactInfo?.weekdayHours && (
                      <div className="flex items-center gap-2">
                        <Clock className="h-3 w-3 sm:h-4 sm:w-4 text-gray-400" />
                        <span>{t("contactPage.quickHelp.weekdays")}: {contactInfo.weekdayHours}</span>
                      </div>
                    )}
                    {contactInfo?.weekendHours && (
                      <div className="flex items-center gap-2">
                        <Clock className="h-3 w-3 sm:h-4 sm:w-4 text-gray-400" />
                        <span>{t("contactPage.quickHelp.weekends")}: {contactInfo.weekendHours}</span>
                      </div>
                    )}
                    {!contactInfo?.weekdayHours && !contactInfo?.weekendHours && (
                      <p className="text-xs sm:text-sm text-gray-500">{t("contactPage.quickHelp.businessHoursNotSpecified")}</p>
                    )}
                  </div>
                </div>
                <div>
                </div>
              </div>
            </Card>
          </div>
        </div>
          </>
        )}
        </div>
      </div>

      {/* Footer - Always at bottom */}
      <SharedFooter3 />
    </div>
  );
};

export default ContactPage;